import React from 'react';
import AnimatedWrapper from '../AnimatedWrapper';
import { CheckCircleIcon } from '../icons/CheckCircleIcon';

const BuyersGuidePage: React.FC = () => {
    return (
        <div className="bg-white min-h-screen font-sans text-gray-900">
            {/* Hero Section - Split Screen */}
            <section className="flex flex-col lg:flex-row min-h-[80vh] border-b border-gray-200">
                <div className="w-full lg:w-1/2 p-8 lg:p-20 flex flex-col justify-center bg-white order-2 lg:order-1">
                    <AnimatedWrapper>
                        <span className="block text-sm tracking-[0.2em] text-gray-500 mb-6 uppercase">The Guide</span>
                        <h1 className="text-5xl lg:text-7xl font-light tracking-tight mb-8 leading-[1.1]">
                            Welcome<br />buyers
                        </h1>
                        <div className="h-1 w-20 bg-black mb-8"></div>
                    </AnimatedWrapper>
                </div>
                <div className="w-full lg:w-1/2 h-[50vh] lg:h-auto bg-gray-100 order-1 lg:order-2 relative overflow-hidden">
                    <img loading="lazy" src="https://images.unsplash.com/photo-1570129477492-45c003edd2be?q=80&w=2070&auto=format&fit=crop"
                        alt="Real Estate Calicut buy sell rent properties online"
                        className="absolute inset-0 w-full h-full object-cover grayscale-0 hover:grayscale transition-all duration-1000"
                    />
                </div>
            </section>

            {/* Intro Section - Centered Editorial */}
            <section className="py-20 lg:py-32 px-4 lg:px-8 border-b border-gray-200">
                <div className="max-w-4xl mx-auto text-center">
                    <AnimatedWrapper>
                        <p className="text-2xl lg:text-3xl font-light leading-relaxed text-gray-800 mb-8">
                            "Finding the right home or rental in Calicut and nearby areas can be stressful when you deal with multiple brokers and unclear information."
                        </p>
                        <p className="text-lg text-gray-500 font-normal max-w-2xl mx-auto">
                            SellAny.in connects you directly with genuine property owners and supports you through a transparent, step‑by‑step process.
                        </p>
                    </AnimatedWrapper>
                </div>
            </section>

            {/* Chapter 01: How it works */}
            <ChapterSection number="01" title="How SellAny works for buyers">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                    <div className="space-y-4">
                        <span className="text-4xl font-light text-gray-200">A</span>
                        <p className="text-gray-600 leading-relaxed">Browse or enquire for verified owner listings in your preferred locations in Calicut (Calicut city, Mankavu, Kinassery, Methottuthyam, palayi and more).</p>
                    </div>
                    <div className="space-y-4">
                        <span className="text-4xl font-light text-gray-200">B</span>
                        <p className="text-gray-600 leading-relaxed">Share your requirement with our team (budget, location, property type); we match you with suitable owner properties and help coordinate visits.</p>
                    </div>
                    <div className="space-y-4">
                        <span className="text-4xl font-light text-gray-200">C</span>
                        <p className="text-gray-600 leading-relaxed">Once you decide, we guide both sides on agreement, documents, and safe payment practices so your deal stays clear and documented.</p>
                    </div>
                </div>
            </ChapterSection>

            {/* Visual Break */}
            {/* <div className="h-[400px] w-full overflow-hidden relative">
                <img loading="lazy" src="https://images.unsplash.com/photo-1616486338812-3dadae4b4f9d?q=80&w=2070&auto=format&fit=crop"
                    alt="Interior visual"
                    className="absolute inset-0 w-full h-full object-cover"
                />
            </div> */}

            {/* Chapter 02: Preparation */}
            <ChapterSection number="02" title="Steps before you visit a property">
                <div className="space-y-8 max-w-3xl">
                    <ListItem>
                        Fix your budget and must‑have criteria: number of bedrooms, parking, water, lift, nearby schools, hospitals, malls, and workplace connectivity.
                    </ListItem>
                    <ListItem>
                        Ask key questions on call/WhatsApp before visiting: total rent/price, deposit, maintenance charges, included furniture, and available date.
                    </ListItem>
                    <ListItem>
                        Check basic documents at least in photo form: ownership proof, ID of owner, and building approvals for apartments whenever possible.
                    </ListItem>
                </div>
            </ChapterSection>

            {/* Chapter 03: The Visit */}
            <ChapterSection number="03" title="During the property visit">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div className="space-y-8">
                        <ListItem>
                            Inspect water, ventilation, common areas, parking, and approach road; for apartments, also check lift, association rules, and security.
                        </ListItem>
                        <ListItem>
                            Take clear photos and note pros/cons so you can compare options calmly later instead of deciding in a rush.
                        </ListItem>
                        <ListItem>
                            Clarify all terms directly with the owner in front of our team representative or over a documented chat so there is no confusion later.
                        </ListItem>
                    </div>
                    <div className="hidden lg:block h-[400px] bg-gray-50 overflow-hidden relative grayscale hover:grayscale-0 transition-all duration-700">
                        <img loading="lazy" src="https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?q=80&w=2070&auto=format&fit=crop"
                            alt="Property Inspection"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                    </div>
                </div>
            </ChapterSection>

            {/* Chapter 04: Closing */}
            <ChapterSection number="04" title="Closing the deal safely" isLast>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="border-t border-gray-900 pt-6">
                        <p className="text-gray-600 leading-relaxed">Make sure rent/price, deposit, notice period, maintenance charges, painting/repair responsibilities, and renewal terms are clearly written in the agreement.</p>
                    </div>
                    <div className="border-t border-gray-900 pt-6">
                        <p className="text-gray-600 leading-relaxed">Never pay large amounts in cash without proper receipts and a signed agreement; prefer bank transfer or other traceable methods.</p>
                    </div>
                    <div className="border-t border-gray-900 pt-6">
                        <p className="text-gray-600 leading-relaxed">For bigger deals (sale/purchase), consider taking an independent legal opinion on the agreement and title documents before final payment.</p>
                    </div>
                </div>
            </ChapterSection>

        </div>
    );
};

// --- Sub-Components for this Layout ---

const ChapterSection: React.FC<{ number: string; title: string; children: React.ReactNode; isLast?: boolean }> = ({ number, title, children, isLast }) => (
    <section className={`py-16 lg:py-24 px-4 lg:px-8 border-t border-gray-900 ${isLast ? 'border-b' : ''}`}>
        <div className="container mx-auto">
            <AnimatedWrapper>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
                    <div className="lg:col-span-4">
                        <span className="block text-6xl font-thin text-gray-200 mb-4">{number}</span>
                        <h2 className="text-3xl font-bold tracking-tight">{title}</h2>
                    </div>
                    <div className="lg:col-span-8 pt-4">
                        {children}
                    </div>
                </div>
            </AnimatedWrapper>
        </div>
    </section>
);

const ListItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="flex gap-6 items-start group">
        <div className="mt-1 flex-shrink-0">
            <div className="w-2 h-2 bg-gray-900 rounded-none group-hover:scale-150 transition-transform"></div>
        </div>
        <p className="text-lg text-gray-600 leading-relaxed">{children}</p>
    </div>
);

export default BuyersGuidePage;
